import { assert, hasElements, isDefined } from '@rhim/utils';

export type SupportedLanguageIsoCode = 'en' | 'de' | 'zh_CN' | 'pt_BR' | 'es';
export type SupportedLanguageUnicodeCode = 'en' | 'de' | 'zh-CN' | 'pt-BR' | 'en-US' | 'en-GB' | 'es';
export const SUPPORTED_LANGUAGES: NonEmptyArray<SupportedLanguageIsoCode> = ['en', 'pt_BR', 'zh_CN', 'de', 'es'];

export const SUPPORTED_REGION_LOCALES: NonEmptyArray<Partial<SupportedLanguageUnicodeCode>> = ['en-US', 'en-GB', 'pt-BR', 'zh-CN'];
export const SUPPORTED_GENERIC_LOCALES: NonEmptyArray<SupportedLanguageUnicodeCode> = ['en', 'de', 'es'];
export const FALLBACK_LANGUAGE_CODE: SupportedLanguageIsoCode = 'en';
export const LOCALE_KEY = 'locale';

export const getAcceptedLanguages = function () {
  if (typeof window === 'undefined') {
    return [FALLBACK_LANGUAGE_CODE];
  }

  const hasNavigatorLanguages = hasElements(window.navigator.languages);
  assert.silent(hasNavigatorLanguages, `window.navigator.languages not defined. Using [${FALLBACK_LANGUAGE_CODE}] instead.`);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return hasNavigatorLanguages ? window.navigator.languages : [FALLBACK_LANGUAGE_CODE];
};

const findMatchingLanguage = (
  navigatorLanguages: readonly string[],
  supportedLocales: NonEmptyArray<SupportedLanguageUnicodeCode>,
  fallbackLocales?: NonEmptyArray<SupportedLanguageUnicodeCode>
): SupportedLanguageUnicodeCode | null => {
  for (const browserLanguage of navigatorLanguages) {
    // First, search in supported locales.
    let matchedLocale = supportedLocales.find((locale) => browserLanguage.startsWith(locale));

    if (matchedLocale) {
      return matchedLocale;
    }

    if (isDefined(fallbackLocales)) {
      // If not found, search in fallback locales if given
      matchedLocale = fallbackLocales.find((locale) => browserLanguage.startsWith(locale));

      if (matchedLocale) {
        return matchedLocale;
      }
    }
  }
  return null;
};

/**
 * Returns the browser language that's supported in the app.
 * For languages with region, if the region is not found in the `SUPPORTED_REGION_LOCALES` it will search for the generic language in the `SUPPORTED_GENERIC_LOCALES`,
 * if that matches, the generic language is returned.
 * E.g: if browser has 'de-DE', the method with return 'de' which is the generic language supported in our app.
 */
export const getBrowserLocale = (): SupportedLanguageUnicodeCode => {
  const acceptedLanguages = getAcceptedLanguages();
  const matchingLanguage = findMatchingLanguage(acceptedLanguages, SUPPORTED_REGION_LOCALES, SUPPORTED_GENERIC_LOCALES);

  return matchingLanguage || FALLBACK_LANGUAGE_CODE;
};

export const getNavigatorLanguage = (
  navigatorLanguages: readonly string[],
  supportedLanguages: NonEmptyArray<SupportedLanguageIsoCode>,
  fallbackLanguage: SupportedLanguageIsoCode
): SupportedLanguageIsoCode => {
  const matchingLanguage = findMatchingLanguage(
    navigatorLanguages,
    supportedLanguages.map((lang) => toUnicodeLocale(lang))
  );
  return isDefined(matchingLanguage) ? toIsoCodeLocale(matchingLanguage) : fallbackLanguage;
};

/**
 * Transform ISO locales to Unicode locales.
 *
 * ISO locales in this codebase commonly use underscores,
 * whereas the Unicode locales the Web platform rely on hyphens instead.
 *
 * @example
 *
 * ```ts
 * toUnicodeLocale('pt_BR'); // => "pt-BR"
 *
 * @example
 *
 * ```ts
 * toUnicodeLocale('en'); // => "en"
 * ```
 */
export function toUnicodeLocale(locale: SupportedLanguageIsoCode): SupportedLanguageUnicodeCode {
  return locale.replace('_', '-') as SupportedLanguageUnicodeCode;
}

export function toIsoCodeLocale(locale: SupportedLanguageUnicodeCode): SupportedLanguageIsoCode {
  return locale.replace('-', '_') as SupportedLanguageIsoCode;
}

export function setHtmlLang(locale: string): void {
  document.querySelector('html')?.setAttribute('lang', locale);
}
